/**
 * Sessie — wie is er ingelogd, en met welke rol.
 *
 * De rol kwam tot nu toe uit localStorage. Daardoor had een `update` op
 * `public.profiles` geen enkel effect op wat de app liet zien: de app keek daar
 * simpelweg nooit naar. Vanaf hier is de profielrij de enige bron.
 *
 * Bewust een module en geen React-hook: de dataLayer moet dit ook kunnen lezen
 * en die is framework-vrij.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';

/**
 * VERVALLEN: `SUPABASE_AUTH_ENABLED` (augustus 2026 verwijderd).
 *
 * De vlag stond al sinds de omzetting hard op `true` en er was niets meer om
 * naar terug te vallen — `accounts/logic/authService` is verwijderd. Wat
 * overbleef was een schakelaar die suggereerde dat er een tweede stand
 * bestond, met bij elke aanroep een `else`-tak die nooit draaide en dus ook
 * nooit werd getest.
 *
 * Zolang de vlag bestond, was hij wél nuttig: hij markeerde precies welke tak
 * dood was. Daarom ging hij als laatste weg, ná de code die hij aanwees.
 */

/** Demo-accounts bestaan alleen lokaal en raken de database nooit. */
export function isDemoAccountId(accountId: string | undefined): boolean {
  return accountId ? accountId.startsWith('demo') : false;
}
export type ProfileRole = 'learner' | 'parent' | 'teacher' | 'admin';
export type DataController = 'school' | 'self';
export type ConsentStatus = 'local' | 'pending' | 'granted' | 'withdrawn';

/**
 * Het profiel zoals de app het nodig heeft. 
 *
 * Bewust géén credential-velden (zoals `recovery_code_hash`): die horen nergens
 * in het geheugen van de client thuis.
 */
export interface SessionProfile {
  id: string;
  role: ProfileRole;
  displayName: string | null;
  grade: number | null;
  username: string | null;
  dataController: DataController;
  consentStatus: ConsentStatus;
  /**
   * De koppelcode waarmee een ouder dit kind aan zich verbindt.
   *
   * Hoort bij het profiel op de SERVER, niet bij de lokale accountstaat. Werd
   * die niet meegenomen in de projectie, dan verdween de code uit de
   * instellingen zodra auth via Supabase liep — en daarmee de enige route om
   * een solo-kind aan een ouder te koppelen.
   */
  linkCode: string | null;
  /**
   * Het e-mailadres van de ingelogde gebruiker.
   *
   * Staat in `auth.users`, NIET in `profiles` — bewust, want een adres is een
   * inloggegeven en hoort niet in een tabel die anderen mogen lezen. Werd het
   * niet uit de sessie meegenomen, dan toonde het ouderportaal "niet
   * ingesteld" terwijl er gewoon mee was ingelogd.
   *
   * Bij leerlingen is dit het synthetische `.invalid`-adres; dat tonen we
   * nergens, want het is geen echt adres.
   */
  email: string | null;
  /**
   * Verificatiestand van een leerkracht (`profiles.teacher_status`, 0020).
   *
   * Werd niet meegenomen, en dat was het gat: de database houdt een
   * niet-geverifieerde leerkracht bij élke leerling weg, maar de leerkracht zelf
   * kon dat nergens zien. Hij logde gewoon in, zag een lege klas, en kreeg geen
   * enkele uitleg. Een stille blokkade leest als een defecte app.
   *
   * Alleen gevuld bij `role = 'teacher'`; bij andere rollen `null`.
   */
  teacherStatus: 'pending' | 'verified' | 'rejected' | null;
  /**
   * De school van een leerkracht. Leeg = geen schoolbinding (0038).
   *
   * Sinds 0041 kan dit uit twee bronnen komen: de naam uit het register bij een
   * KEUZE, of de vrije tekst bij een AANVRAAG. `schoolFromRegistry` zegt welke
   * van de twee, en dat verschil bepaalt wat een wachtende leerkracht kan doen.
   */
  schoolName: string | null;
  /** True = de school is uit het register gekozen en dus natrekbaar (0041). */
  schoolFromRegistry: boolean;
}
export type SessionState = /** Nog aan het herstellen bij het opstarten. Kort, maar echt bestaand. */
{
  status: 'loading';
}
/** Geen sessie — de app draait lokaal, zoals vroeger. */ | {
  status: 'anonymous';
} | {
  status: 'authenticated';
  profile: SessionProfile;
}
/**
 * Ingelogd, maar geen bruikbaar profiel. Bewust een APARTE toestand: dit als
 * 'anonymous' behandelen zou een inlogscherm tonen aan iemand die al is
 * ingelogd — een onoplosbare lus.
 *
 * `reason` scheidt twee gevallen die eerst één melding deelden, en dat scheelt
 * de gebruiker een verkeerde conclusie:
 *
 *   • 'missing' — de query slaagde en gaf nul rijen. Het profiel bestaat echt
 *     niet (mislukte signup-trigger). Opnieuw proberen helpt niet; dit moet
 *     hersteld worden.
 *   • 'error'   — de query zelf mislukte (netwerk, tijdelijke storing). Of het
 *     profiel bestaat weten we niet. Opnieuw proberen is hier juist WEL de
 *     oplossing, en dat is de meest voorkomende van de twee.
 *
 * Ze samenvoegen betekende dat een haperend netwerk werd gepresenteerd als een
 * kapot account — met "neem contact op met de beheerder" als advies, terwijl
 * één keer opnieuw proberen genoeg was.
 */ | {
  status: 'incomplete';
  userId: string;
  reason: 'missing' | 'error';
};
let current: SessionState = {
  status: 'loading'
};
const listeners = new Set<(s: SessionState) => void>();
function setState(next: SessionState): void {
  current = next;
  for (const l of listeners) l(next);
}

/** Huidige staat, synchroon. Voor de dataLayer, die niet kan wachten. */
export function getSessionState(): SessionState {
  return current;
}
export function getActiveProfile(): SessionProfile | null {
  return current.status === 'authenticated' ? current.profile : null;
}

/** Abonneer op sessiewijzigingen. Retourneert een unsubscribe-functie. */
export function subscribeToSession(listener: (s: SessionState) => void): () => void {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
}
/**
 * De velden die de app nodig heeft om te WERKEN. Zonder deze is er geen
 * bruikbaar profiel en heeft inloggen geen zin.
 */
const CORE_PROFILE_COLUMNS = 'id, role, display_name, grade, username, data_controller, consent_status, link_code';

/**
 * Velden die pas door een latere migratie zijn toegevoegd.
 *
 * Deze staan APART omdat een `select` in Postgres alles-of-niets is: één kolom
 * die nog niet bestaat laat de héle query falen met 42703, en dan is er geen
 * profiel — dus geen sessie, dus val je terug op wat er lokaal stond. Dat
 * gebeurde: `school_name` (0038) niet gedraaid, en een kind zag een ander
 * account. Eén nieuwe kolom die nog niet overal is uitgerold hoort niemand te
 * kunnen buitensluiten, dus vragen we ze in een tweede poging apart op.
 */
const OPTIONAL_PROFILE_COLUMNS = ['teacher_status', 'school_name', 'school_id'];
async function loadProfile(userId: string, email?: string | null): Promise<void> {
  let {
    data,
    error
  } = await supabase.from('profiles').select([CORE_PROFILE_COLUMNS, ...OPTIONAL_PROFILE_COLUMNS].join(', ')).eq('id', userId).maybeSingle();

  // 42703 = undefined_column. De optionele velden ontbreken; opnieuw met alleen
  // de kern. Bewust alleen op DEZE code — elke andere fout is een echte storing
  // en moet zichtbaar blijven, niet weggevangen worden door een tweede poging.
  if (error && (error as {
    code?: string;
  }).code === '42703') {
    logError('[Session] Profielkolom ontbreekt — migratie niet gedraaid. Verder met de kernvelden.', error);
    ({
      data,
      error
    } = await supabase.from('profiles').select(CORE_PROFILE_COLUMNS).eq('id', userId).maybeSingle());
  }
  if (error) {
    logError('[Session] Profiel laden mislukt', error);
    // Een netwerkfout is iets anders dan een ontbrekend profiel. We weten het
    // niet zeker, dus behandelen we het als incompleet in plaats van iemand uit
    // te loggen — uitloggen bij een haperend netwerk is vijandig gedrag.
    setState({
      status: 'incomplete',
      userId,
      reason: 'error'
    });
    return;
  }
  if (!data) {
    setState({
      status: 'incomplete',
      userId,
      reason: 'missing'
    });
    return;
  }
  /*
   ── De naam van een GEKOZEN school staat niet op het profiel ────────────────

   Sinds 0041 is `profiles.school_name` uitsluitend de AANVRAAG. Koos de
   leerkracht een school uit het register, dan is die kolom leeg en staat de naam
   in `schools`. Zou dit blok ontbreken, dan zag een leerkracht die netjes een
   school koos in zijn eigen instellingen "geen school" staan — en las de
   verificatiebanner dat hij op óns wacht in plaats van op zijn collega's.

   Een aparte query en geen embedded join: bestaat `school_id` nog niet (0041
   niet gedraaid), dan zou een join de héle profielquery laten falen met een
   andere foutcode dan de 42703 die hierboven wordt opgevangen. Dan valt niemand
   meer in te loggen. Dit kost één extra verzoek voor precies de leerkrachten die
   een school kozen.
   */
  const rawSchoolId = (data as Record<string, unknown>).school_id as string | null | undefined;
  let registrySchoolName: string | null = null;
  if (data.role === 'teacher' && rawSchoolId) {
    const {
      data: school
    } = await supabase.from('schools').select('name').eq('id', rawSchoolId).maybeSingle();
    // Mislukt dit, dan blijft `registrySchoolName` null en valt de naam terug op
    // `school_name`. Dat is leeg bij een keuze, dus de leerkracht ziet "geen
    // school" — onhandig, maar het is geen onwaarheid en het blokkeert niets.
    registrySchoolName = (school?.name as string | null) ?? null;
  }
  setState({
    status: 'authenticated',
    profile: {
      id: data.id as string,
      role: data.role as ProfileRole,
      displayName: data.display_name as string | null ?? null,
      grade: data.grade as number | null ?? null,
      username: data.username as string | null ?? null,
      dataController: data.data_controller as DataController ?? 'self',
      consentStatus: data.consent_status as ConsentStatus ?? 'local',
      linkCode: data.link_code as string | null ?? null,
      // Synthetische leerlingadressen tonen we nergens — dat is geen echt
      // adres en zou alleen maar verwarren.
      email: email && !email.endsWith('.invalid') ? email : null,
      // De kolom heeft `default 'pending'` voor élke rol (0020), maar buiten
      // een leerkracht betekent hij niets. Hem daar op null zetten voorkomt dat
      // een ouder ooit een "wacht op verificatie"-melding krijgt.
      teacherStatus: data.role === 'teacher' ? (data as Record<string, unknown>).teacher_status as 'pending' | 'verified' | 'rejected' | null ?? 'pending' : null,
      // Register vóór aanvraag: een gekozen school is het sterkste gegeven dat
      // we hebben, en de aanvraagtekst is bij een keuze leeg.
      schoolName: data.role === 'teacher' ? registrySchoolName ?? (data as Record<string, unknown>).school_name as string | null ?? null : null,
      /**
       * Heeft deze leerkracht zijn school uit het register GEKOZEN?
       *
       * De verificatiebanner gebruikt dit: bij een keuze wacht hij op zijn
       * collega's/ons met een natrekbare school, bij een aanvraag wacht hij
       * eerst tot wij die school opnemen. Dat verschil bepaalt wat hij nú kan
       * doen, en zonder dat onderscheid leest elke wachttijd hetzelfde.
       */
      schoolFromRegistry: data.role === 'teacher' ? Boolean(rawSchoolId) : false
    }
  });
}

/**
 * Herlaad het profiel van de huidige sessie.
 *
 * Direct na inloggen aangeroepen: zonder deze await navigeert de app voordat de
 * sessie het profiel kent, en draait de routing te vroeg.
 */
export async function refreshProfile(): Promise<void> {
  const {
    data
  } = await supabase.auth.getSession();
  if (!data.session?.user) {
    setState({
      status: 'anonymous'
    });
    return;
  }
  await loadProfile(data.session.user.id, data.session.user.email);
}
let initialised = false;

/** Start sessieherstel en abonneer op auth-wijzigingen. Idempotent. */
export function initSessionContext(): void {
  if (initialised) return;
  initialised = true;
  void (async () => {
    try {
      const {
        data
      } = await supabase.auth.getSession();
      if (data.session?.user) await loadProfile(data.session.user.id, data.session.user.email);else setState({
        status: 'anonymous'
      });
    } catch (err) {
      // Zonder dit vangnet blijft de app eeuwig op 'loading' en ziet de
      // gebruiker alleen een spinner.
      logError('[Session] Sessieherstel mislukt', err);
      setState({
        status: 'anonymous'
      });
    }
  })();
  supabase.auth.onAuthStateChange((event, session) => {
    // Een verlopen of ingetrokken refresh-token levert geen sessie meer op.
    // Dat is geen fout maar het normale einde van een sessie: netjes naar
    // 'anonymous', zodat de app het inlogscherm toont in plaats van te blijven
    // hangen met een token dat nergens meer geldig is.
    if (event === 'SIGNED_OUT' || !session?.user) {
      setState({
        status: 'anonymous'
      });
      return;
    }
    // TOKEN_REFRESHED levert dezelfde gebruiker; opnieuw ophalen is onnodig.
    if (event === 'TOKEN_REFRESHED' && current.status === 'authenticated') return;
    void loadProfile(session.user.id, session.user.email);
  });
}

/**
 * Mag er voor dit profiel naar de server worden geschreven?
 *
 * Bij twijfel NEE. Die asymmetrie is bewust: onterecht lokaal opslaan kost
 * hooguit synchronisatie, onterecht naar de server schrijven verwerkt
 * kinderdata zonder grondslag — en dat is niet terug te draaien.=
 */
/**
 * Wie kan de voortgang van de ingelogde LEERLING zien?
 *
 * Bewust een aparte query in plaats van afgeleide lokale staat: een kind moet
 * kunnen zien wie er meekijkt, ook op een apparaat waar die ouder nooit heeft
 * ingelogd. Transparantie over wie toegang heeft is een AVG-eis, geen extraatje
 * — en afgeleide lokale staat kan dat simpelweg niet garanderen.
 */
export interface LinkedPerson {
  id: string;
  name: string;
  role: ProfileRole;
  /** Vanuit de ingelogde gebruiker gezien. */
  relation: 'parent' | 'teacher' | 'child';
  consentStatus: ConsentStatus;
  /**
   * Wie gaf de toestemming? Toestemming hoort bij het KIND, niet bij één
   * ouder — zonder deze velden kan de UI niet onderscheiden tussen "ik gaf
   * toestemming" en "de andere ouder gaf toestemming al".
   */
  consentGrantedBy: string | null;
  consentGrantedByName: string | null;
  consentGrantedAt: string | null;
  /** Aantal gekoppelde ouders. Bepaalt of ontkoppelen de laatste is. */
  parentCount: number;
}
/**
 * Zelfde query, ander faalcontract: `null` bij een fout in plaats van een lege
 * lijst.
 *
 * Dat onderscheid is nodig zodra een aanroeper op basis van deze lijst iets
 * WEGHAALT. Voor een leesscherm is "leeg" een prima benadering van "mislukt" —
 * je ziet even niets. Maar voor een aanroeper die de lokale kinderlijst
 * bijwerkt, betekent een lege lijst bij een haperende wifi dat élke koppeling
 * verdwijnt. Dat is precies de fout die dit project bij het ontkoppelen al
 * eens heeft gemaakt: lokaal wissen op grond van een mislukte serveraanroep.
 *
 * Eén definitie, twee contracten — `fetchLinkedPeople()` hieronder valt hier
 * op terug, zodat de query niet twee keer bestaat en niet uiteen kan lopen.
 */
export async function fetchLinkedPeopleOrNull(): Promise<LinkedPerson[] | null> {
  const {
    data,
    error
  } = await supabase.rpc('get_linked_people');
  if (error) {
    logError('[Session] Gekoppelde personen ophalen mislukt', error);
    return null;
  }
  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    name: r.name as string | null ?? 'Onbekend',
    role: r.role as ProfileRole,
    relation: r.relation as LinkedPerson['relation'],
    consentStatus: r.consent_status as ConsentStatus ?? 'local',
    consentGrantedBy: r.consent_granted_by as string | null ?? null,
    consentGrantedByName: r.consent_granted_by_name as string | null ?? null,
    consentGrantedAt: r.consent_granted_at as string | null ?? null,
    parentCount: r.parent_count as number | null ?? 0
  }));
}

/** Leesvariant: een fout wordt een lege lijst. Zie hierboven waarom dat voor
 * schermen mag en voor opruimen niet. */
export async function fetchLinkedPeople(): Promise<LinkedPerson[]> {
  return (await fetchLinkedPeopleOrNull()) ?? [];
}
export function shouldUseBackend(): boolean {
  const profile = getActiveProfile();
  if (!profile) return false;
  // Schoolroute: de school is verwerkingsverantwoordelijke en heeft een eigen
  // grondslag.
  if (profile.dataController === 'school') return true;
  // Soloroute: wij zijn verantwoordelijke, dus er moet aantoonbare ouderlijke
  // toestemming zijn. 'pending' telt NIET — uitgenodigd is niet gegeven.
  return profile.consentStatus === 'granted';
}

/**
 * Vorm van de accountstaat, structureel getypeerd.
 *
 * Bewust GEEN import uit `../../accounts`: die barrel trekt het hele
 * accounts-domein mee, terwijl deze module ook door de dataLayer gelezen moet
 * kunnen worden — en die is framework- en domeinvrij.
 */
export interface AccountsLike {
  accountsById: Record<string, Record<string, unknown>>;
  activeAccountId: string | null;
  [key: string]: unknown;
}

/**
 * Projecteer het ingelogde profiel als ACTIEF account in de accountstaat.
 *
 * `AuthGuard`, `PortalSwitch` en alle portalen lezen de actieve gebruiker uit
 * `multiAccountState.accounts`. Zonder deze projectie logt iemand in bij
 * Supabase terwijl de app een oud localStorage-account actief houdt — dan zie
 * je de naam van een demo-leerling terwijl je als iemand anders bent ingelogd.
 *
 * Expliciet TIJDELIJK: zodra de portalen de sessie rechtstreeks lezen, kan dit
 * weg. Er wordt géén `passwordHash` gezet — de server verifieert het wachtwoord
 * en een hash hoort niet in de clientstaat thuis.
 */
export function applyProfileToAccounts(accounts: AccountsLike, profile: SessionProfile): AccountsLike {
  const now = Date.now();
  const existing = accounts.accountsById[profile.id];

  // Lokale extra's (groep, onboarding-voortgang) blijven behouden; de
  // identiteitsvelden en de ROL komen van de server.
  const merged: Record<string, unknown> = {
    ...(existing ?? {}),
    id: profile.id,
    role: profile.role,
    displayName: profile.displayName ?? 'Gebruiker',
    createdAt: existing?.createdAt as number | undefined ?? now,
    lastActive: now,
    // Stond hier onvoorwaardelijk op `true`, terwijl het commentaar hierboven
    // beweert dat de onboarding-voortgang behouden blijft. Dat gold dus juist
    // niet: het veld werd bij élke sessie-projectie overschreven.
    //
    // De standaard blijft `true` wanneer er nog geen lokaal account is. Dat is
    // bewust: dat is iemand die op een nieuw apparaat inlogt, en die opnieuw
    // door de wizard sturen is verkeerd. Een verse registratie zet de
    // rondleiding rechtstreeks aan via `pendingAppTour`, niet via dit veld.
    hasCompletedOnboarding: existing?.hasCompletedOnboarding as boolean | undefined ?? true
  };
  if (profile.username) merged.username = profile.username;
  if (profile.grade != null) merged.grade = profile.grade;
  if (profile.linkCode) merged.linkCode = profile.linkCode;
  if (profile.email) merged.email = profile.email;
  // Onvoorwaardelijk bij een leerkracht, óók als de waarde 'verified' is: een
  // account dat van pending naar verified gaat moet de oude waarde kwijtraken.
  // Een `if (…)` zou de vorige status laten staan en de melding eeuwig tonen.
  if (profile.role === 'teacher') {
    merged.teacherStatus = profile.teacherStatus ?? undefined;
    merged.schoolName = profile.schoolName ?? undefined;
  }
  return {
    ...accounts,
    accountsById: {
      ...accounts.accountsById,
      [profile.id]: merged
    },
    activeAccountId: profile.id
  };
}